import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { createUseStyles } from '@/services/createUseStyles';
import { getApiError } from '@/types/apiError';
import { CheckCircle, Error as ErrorIcon, HourglassEmpty } from '@mui/icons-material';
import { Alert, Box, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Paper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useBulkMetadataUpdate } from '../../hooks/useBulkMetadataUpdate';
import { BulkUploadData, UpdateResult } from '../../types';

const useStyles = createUseStyles(theme => ({
  ProcessingStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  ProcessingStep_progress: {
    marginBottom: '24px',
  },
  ProcessingStep_summary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '8px',
  },
  ProcessingStep_logContainer: {
    flex: 1,
    marginBottom: '24px',
    maxHeight: '400px',
    overflow: 'auto',
  },
  ProcessingStep_logItem: {
    borderRadius: '4px',
    marginBottom: '4px',
    '&.success': {
      backgroundColor: '#e8f5e8',
    },
    '&.error': {
      backgroundColor: '#ffeaea',
    },
    '&.processing': {
      backgroundColor: '#fff3cd',
    },
  },
}));

export const ProcessingStep = ({
  data,
  onComplete,
}: {
  data?: BulkUploadData;
  onComplete: (results: UpdateResult[]) => void;
}) => {
  const c = useStyles();
  const [results, setResults] = useState<UpdateResult[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({ completed: 0, total: 0, currentAssetId: '' });
  const hasStartedProcessing = useRef(false);
  const { bulkUpdateAssetMetadataBatch } = useBulkMetadataUpdate();

  useEffect(() => {
    if (!data || isComplete || hasStartedProcessing.current) return;

    hasStartedProcessing.current = true;

    const processUpdates = async () => {
      const matchedAssets = data.assetMatches.filter(
        match =>
          (match.status === 'matched' && match.selectedAsset) || (match.status === 'asset_id_provided' && match.assetId)
      );

      const bulkUpdates = matchedAssets
        .filter(match => data.parsedData.find(r => r.assetName === match.assetName))
        .map(match => {
          const row = data.parsedData.find(r => r.assetName === match.assetName)!;
          const assetId = match.selectedAsset?.id || match.assetId!;
          return {
            assetId,
            metadata: row.metadata,
            assetName: match.assetName,
          };
        });

      if (bulkUpdates.length === 0) {
        setIsComplete(true);
        onComplete([]);
        return;
      }

      try {
        setCurrentProgress({ completed: 0, total: bulkUpdates.length, currentAssetId: '' });

        const initialResults: UpdateResult[] = bulkUpdates.map(update => ({
          assetName: update.assetName,
          assetId: update.assetId,
          status: 'pending' as const,
          updatedFields: update.metadata,
        }));
        setResults(initialResults);

        const apiResults = await bulkUpdateAssetMetadataBatch(
          bulkUpdates.map(({ assetId, metadata }) => ({ assetId, metadata })),
          data.metadataFieldDefinitions,
          (completed, total, currentAssetId) => {
            setCurrentProgress({ completed, total, currentAssetId });

            setResults(previousResults =>
              previousResults.map((result, resultIndex) => {
                if (resultIndex < completed) {
                  return { ...result, status: 'processing' as const };
                }
                return result;
              })
            );
          }
        );

        const finalResults: UpdateResult[] = bulkUpdates.map(update => {
          const apiResult = apiResults.find(r => r.assetId === update.assetId);
          return {
            assetName: update.assetName,
            assetId: update.assetId,
            status: apiResult?.success ? 'success' : 'error',
            updatedFields: update.metadata,
            errorMessage: apiResult?.error,
          };
        });

        setResults(finalResults);
        setIsComplete(true);
        onComplete(finalResults);
      } catch (error) {
        const updateResults: UpdateResult[] = bulkUpdates.map(update => ({
          assetName: update.assetName,
          assetId: update.assetId,
          status: 'error',
          updatedFields: update.metadata,
          errorMessage: getApiError(error as Error)?.message ?? 'Update failed',
        }));

        setResults(updateResults);
        setIsComplete(true);
        onComplete(updateResults);
      }
    };

    processUpdates();
  }, [data, isComplete, bulkUpdateAssetMetadataBatch, onComplete]);

  if (!data) {
    return <Alert severity='error'>No data available. Please go back and upload a file.</Alert>;
  }

  const matchedAssets = data.assetMatches.filter(
    match =>
      (match.status === 'matched' && match.selectedAsset) || (match.status === 'asset_id_provided' && match.assetId)
  );

  const progress = currentProgress.total > 0 ? (currentProgress.completed / currentProgress.total) * 100 : 0;
  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;

  const getStatusIcon = (result: UpdateResult) => {
    switch (result.status) {
      case 'success': {
        return <CheckCircle color='success' />;
      }
      case 'error': {
        return <ErrorIcon color='error' />;
      }
      case 'processing': {
        return <HourglassEmpty color='primary' />;
      }
      default: {
        return <HourglassEmpty />;
      }
    }
  };

  const getProgressText = () => {
    if (isComplete) {
      return `Completed: ${currentProgress.total} of ${matchedAssets.length} assets`;
    }
    if (currentProgress.currentAssetId) {
      return `Processing asset: ${currentProgress.currentAssetId} (${currentProgress.completed} of ${currentProgress.total})`;
    }
    return `Processing ${matchedAssets.length} assets...`;
  };

  return (
    <Box className={c.ProcessingStep_root}>
      <HeaderText gutterBottom>{isComplete ? 'Processing Complete' : 'Processing Updates'}</HeaderText>
      <Box className={c.ProcessingStep_progress}>
        <BodyText gutterBottom>{getProgressText()}</BodyText>
        <LinearProgress variant='determinate' value={progress} sx={{ height: 8, borderRadius: 4 }} />
      </Box>
      <Box className={c.ProcessingStep_summary}>
        <HeaderText gutterBottom>Progress Summary</HeaderText>
        <BodyText color='success.main'>
          <strong>Successful:</strong> {successCount}
        </BodyText>
        <BodyText color='error.main'>
          <strong>Failed:</strong> {errorCount}
        </BodyText>
        <BodyText>
          <strong>Remaining:</strong> {matchedAssets.length - results.length}
        </BodyText>
      </Box>
      {!isComplete && (
        <Alert severity='info' sx={{ marginBottom: 2 }}>
          <BodyText>Processing {matchedAssets.length} assets in bulk. This may take a few moments...</BodyText>
        </Alert>
      )}
      {isComplete && (
        <Alert severity='success' sx={{ marginBottom: 2 }}>
          <BodyText>
            Processing complete! {successCount} assets updated successfully.
            {errorCount > 0 && ` ${errorCount} updates failed.`}
          </BodyText>
        </Alert>
      )}
      <Paper className={c.ProcessingStep_logContainer}>
        <List dense>
          {results.map((result, index) => (
            <ListItem key={`${result.assetName}-${index}`} className={`${c.ProcessingStep_logItem} ${result.status}`}>
              <ListItemIcon>{getStatusIcon(result)}</ListItemIcon>
              <ListItemText
                primary={result.assetName}
                secondary={
                  result.errorMessage
                    ? `Error: ${result.errorMessage}`
                    : `Updated ${Object.keys(result.updatedFields).length} metadata fields`
                }
              />
            </ListItem>
          ))}
          {!isComplete && (
            <ListItem className={`${c.ProcessingStep_logItem} processing`}>
              <ListItemIcon>
                <HourglassEmpty />
              </ListItemIcon>
              <ListItemText
                primary={`Processing ${matchedAssets.length} assets...`}
                secondary='Bulk update in progress'
              />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};
